import buildPromiseResolver from "../util/promise-resolver";
const RpcErrorCode = {
    MissingMethod: 'RpcMethodNotFound',
    MethodNotPermitted: 'RpcMethodNotPermitted',
    MethodNameRequired: 'RpcMethodNameRequired'
};
const DefaultDelegateOpts = {
    ignoreWithUnderscorePrefix: true,
    ignoreInherited: true,
};
export class RequestHandler {
    constructor() {
        this._requestHandlerDelegateHolders = [];
        this._namedRequestHandlerHolderByMethodName = {};
    }
    addRequestHandlerDelegate(delegate, opts) {
        if (typeof delegate !== 'object') {
            throw new Error('Expecting an object containing request handlers');
        }
        opts = Object.assign({ context: delegate }, DefaultDelegateOpts, opts);
        if (Array.isArray(opts.shouldRun)) {
            opts.shouldRun = new Set(opts.shouldRun);
        }
        this._requestHandlerDelegateHolders.push({ delegate, opts });
    }
    addRequestHandler(methodName, fn, opts = {}) {
        // note: intentionally accepts "_" prefix method names
        this._namedRequestHandlerHolderByMethodName[methodName] = Object.assign({ fn }, opts);
    }
    addRequestHandlers(fnByMethodName, opts) {
        Object.keys(fnByMethodName).forEach((methodName) => {
            if (methodName.charAt(0) !== '_') {
                this.addRequestHandler(methodName, fnByMethodName[methodName], opts);
            }
        });
    }
    onValidatedRequest(methodName, userArgs, endpointUid) {
        if (!methodName) {
            return Promise.reject({ errorCode: RpcErrorCode.MethodNameRequired, methodName });
        }
        if (methodName === 'constructor') {
            return Promise.reject({ errorCode: RpcErrorCode.MethodNotPermitted, methodName });
        }
        userArgs = userArgs || [];
        const namedHolder = this._namedRequestHandlerHolderByMethodName[methodName];
        let { fn, context, useCallback } = (namedHolder || {});
        if (!fn) {
            const delegateHolder = this._requestHandlerDelegateHolders.find((holder) => {
                const { delegate, opts } = holder;
                if (typeof delegate[methodName] !== 'function') {
                    return false;
                }
                if (opts.ignoreWithUnderscorePrefix && methodName.charAt(0) === '_') {
                    return false;
                }
                if (opts.ignoreInherited && !(delegate.hasOwnProperty(methodName) || Object.getPrototypeOf(delegate).hasOwnProperty(methodName))) {
                    return false;
                }
                if (_isPermissionSet(opts.shouldRun) && !opts.shouldRun.has(methodName)) {
                    return false;
                }
                else if (typeof opts.shouldRun === 'function' && opts.shouldRun.call(null, methodName, delegate, ...userArgs) !== true) {
                    return false;
                }
                return true;
            });
            if (delegateHolder) {
                fn = delegateHolder.delegate[methodName];
                context = delegateHolder.opts.context === undefined ? delegateHolder.delegate : delegateHolder.opts.context;
            }
        }
        if (!fn) {
            return Promise.reject({
                errorCode: RpcErrorCode.MissingMethod,
                remoteMethodName: methodName,
            });
        }
        else {
            try {
                if (useCallback) {
                    const { promise, resolve, reject } = buildPromiseResolver();
                    userArgs.push((err, ...results) => {
                        if (err) {
                            reject(err);
                        }
                        else {
                            resolve(...results);
                        }
                    });
                    fn.apply(context, userArgs);
                    return promise;
                }
                else {
                    return Promise.resolve(fn.apply(context, userArgs)).catch(reason => {
                        if (typeof reason === 'object' && (reason.stack || reason instanceof Error)) {
                            return Promise.reject(this._applyUncaughtErrorData(reason, { remoteMethodName: methodName }, endpointUid));
                        }
                        else {
                            return Promise.reject(reason);
                        }
                    });
                }
            }
            catch (error) {
                // todo: use debugHandler { message: `Uncaught error in "${methodName}" request handler`, error }
                return Promise.reject(this._applyUncaughtErrorData(error, { remoteMethodName: methodName }, endpointUid));
            }
        }
    }
    _applyUncaughtErrorData(err, extra, endpointUid) {
        const { message, fileName, lineNumber } = err;
        if (err && typeof err.toJSON === 'function') {
            err = err.toJSON();
        }
        return Object.assign({
            errorCode: err.errorCode || (!err.name || err.name === 'Error' ? 'UncaughtError' : err.name),
            endpointUid,
        }, err, { message, fileName, lineNumber }, extra);
    }
}
function _isPermissionSet(obj) {
    return obj && ['has', 'keys'].every(k => typeof obj[k] === 'function');
}
export { RpcErrorCode, };
//# sourceMappingURL=request-handler.js.map